<!--赖传峰-->
<!--2022/11/9-->
<template>
  <div
    v-if="internalVisible"
    class="resolve-source"
  >
    <div class="thumbnail-and-operation">
      <template v-if="state.processing && url">
        <div class="content processing">
          <a
            class="close"
            href="javascript:void(0)"
            @click="internalVisible = false"
          />
          <div class="animation">
            <LottieAnimation
              :animation-data="lottieData"
              :autoplay="true"
            />
          </div>
        </div>
        <div class="description">
          <div class="title">
            {{ t('resolve-source-processing-title') }}
          </div>
          <div class="subtitle">
            {{ t('resolve-source-processing-subtitle') }}
          </div>
          <div class="processing-text">
            <div class="pp-spin" />
            {{ t('resolve-source-processing-text') }}
          </div>
        </div>
      </template>
      <template v-else>
        <div
          :class="isUseEmbed ? 'embed' : undefined"
          class="content"
        >
          <a
            class="close"
            href="javascript:void(0)"
            @click="internalVisible = false"
          />
          <div
            v-if="isNormalResource"
            class="watermark"
          >
            image from {{ sourceFrom }}
          </div>
          <div
            v-if="!isUseEmbed"
            class="thumbnail-wrap"
          >
            <div
              v-if="!isNormalResource"
              class="thumbnail"
            >
              <div class="thumbnail__sensitive" />
            </div>
            <div
              v-else
              class="thumbnail"
            >
              <div
                v-if="state.thumbnail"
                :style="{'background-image': `url(${state.thumbnail})`}"
                class="thumbnail__blur"
              />
              <ElImage
                :src="state.thumbnail"
                fit="contain"
              >
                <template #placeholder>
                  <div class="thumbnail__placeholder" />
                </template>
                <template #error>
                  <div class="thumbnail__error" />
                </template>
              </ElImage>
              <div class="info">
                <div>
                  <div class="file-icon">
                    <ElImage
                      v-if="state.icon"
                      :src="state.icon"
                    />
                  </div>
                  <div style="min-width: 0">
                    <div class="name">
                      {{ state.name }}
                    </div>
                    <div>
                      <span
                        v-if="state.size !== '0B'"
                        class="file-size"
                      >
                        {{ state.size }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <EmbedSource
            v-else
            :url="url"
          />
        </div>
        <div class="description">
          <div class="title">
            <div
              v-if="titleConfig.icon"
              :style="{'background-image': `url(${titleConfig.icon})`}"
              class="icon"
            />
            {{ t(titleConfig.title) }}
          </div>
          <div class="subtitle">
            {{ t(titleConfig.subtitle) }}
          </div>

          <ul
            v-if="isNormalResource"
            class="tips"
          >
            <li>{{ t('resolve-source-done-tip-1') }}</li>
            <li>{{ t('resolve-source-done-tip-2') }}</li>
            <li>
              {{
                state.speedSave
                  ? t('resolve-source-done-tip-3-speed-save', [estimateSaveTime])
                  : t('resolve-source-done-tip-3')
              }}
            </li>
          </ul>
          <a
            v-if="!isNormalResource"
            class="btn-save-now"
            href="javascript:void(0)"
            @click="internalVisible = false"
          >
            {{ $t('i-knows') }}
          </a>
          <a
            v-else
            class="btn-save-now"
            href="javascript:void(0)"
            @click="toSaveResource"
          >
            {{ t('resolve-source-save-now') }}
          </a>
        </div>
      </template>
    </div>
    <div
      v-if="isNormalResource"
      class="disclaimer"
    >
      <i18n-t
        keypath="disclaimer"
        tag="div"
      >
        <template #disclaimerHeader>
          <strong>{{ $t('disclaimer-header') }}</strong>
        </template>
        <template #whatsLink>
          <a
            :href="`https://${sourceFrom}`"
            target="_blank"
          >
            {{ sourceFrom }}
          </a>
        </template>
        <template #reportInDisclaimer>
          <a
            href="javascript:void(0)"
            @click="showReportDialog"
          >
            {{ $t('report-in-disclaimer') }}
          </a>
        </template>
        <template #copyrightInDisclaimer>
          <a
            href="https://copyright.mypikpak.com"
            target="_blank"
          >
            {{ $t('copyright-in-disclaimer') }}
          </a>
        </template>
      </i18n-t>
    </div>
  </div>
  <ClientOnly>
    <ReportResourceDialog
      v-if="props.url"
      v-model:visible="reportDialogVisible"
      :resource-url="props.url"
    />
  </ClientOnly>
</template>

<script
  lang="ts"
  setup
>
import {
  computed,
  inject,
  reactive,
  ref,
  useI18n,
  watch,
  watchEffect,
} from '#imports'
import errorIcon from '@/assets/images/home/error-icon.svg'
import $track from '@/track/webOfficialWebsite'
import { stringifyQuery } from 'vue-router'
import EmbedSource from '~/components/home/resolve-source/EmbedSrouce.vue'
import ReportResourceDialog from '~/components/home/resolve-source/ReportResourceDialog.vue'
import {
  resolveResource,
  ResourceStatus,
} from '~/components/home/resolve-source/resolve.script'
import {
  InjectionKey,
  SceneInQuery4Track,
} from '~/types/enum'
import {
  authByLocalStorage,
  sleep,
  storageUnit,
} from '~/utils'

const props = defineProps<{
  url?: string
  visible: boolean
}>()
const lottieData = async () => (await import('~/components/home/lottie-data/resolve-source.json')).default
const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)
const reportDialogVisible = ref(false)

const showReportDialog = () => {
  if (!authByLocalStorage()) return showLoginDialog?.({ afterLogin: () => reportDialogVisible.value = true })
  reportDialogVisible.value = true
}

// eslint-disable-next-line no-spaced-func
const emit = defineEmits<{
  (e: 'update:visible', visible: boolean): void,
  (e: 'closed'): void,
  (e: 'error', message: string): void
}>()

type State = {
  processing: boolean
  name?: string
  size?: string
  sizeWithoutUnit?: number // 文件的大小，用于判断解析是否成功
  icon?: string
  fileCount?: number
  thumbnail?: string
  speedSave: boolean
  status?: ResourceStatus // 资源状态
}

const state = reactive<State>({
  processing: false,
  name: undefined,
  size: undefined,
  sizeWithoutUnit: undefined,
  icon: undefined,
  fileCount: undefined,
  thumbnail: undefined,
  speedSave: false,
  status: ResourceStatus.OK,
})

const toSaveResource = () => {
  if (!props.url) return
  $track.web_official_save_click({
    result: state.thumbnail
      ? state.speedSave
        ? 'pic_show_old'
        : 'pic_show_new'
      : 'pic_fail',
  })

  const queryString = stringifyQuery({
    action: 'create_task',
    url: encodeURIComponent(props.url),
    scene: SceneInQuery4Track.OFFICIAL_WEBSITE,
    speed_save: state.speedSave ? 1 : undefined, // 是否秒存，这个字段供亲手任务模块使用，创建任务模块不需要
  })
  const url = `/drive/all?${queryString}`
  if (!authByLocalStorage()) {
    return showLoginDialog?.({
      afterLogin: () => window.open(url),
      scene: 'main_page_save',
    })
  }
  return window.open(url)
}

const isNormalResource = computed(() => !state.processing && state.status === ResourceStatus.OK)
const isUseEmbed = computed(() => !state.processing && props.url?.startsWith('https') && isNormalResource.value && !state.sizeWithoutUnit)
const sourceFrom = computed(() => (isUseEmbed.value ? 'embed.ly' : 'whatslink.info'))
const titleConfig = computed(() => {
  switch (state.status) {
    case ResourceStatus.LIMITED:
      return {
        title: 'resolve-sensitive-resource-title',
        subtitle: 'resolve-sensitive-resource-subtitle',
        icon: errorIcon,
      }
    case ResourceStatus.UNKNOWN:
      return {
        title: 'resolve-unready-resource-title',
        subtitle: 'resolve-unready-resource-subtitle',
        icon: errorIcon,
      }
    default:
      return {
        title: 'resolve-source-done-title',
        subtitle: 'resolve-source-processing-subtitle',
        icon: undefined,
      }
  }
})

const internalVisible = computed({
  get() {
    return props.visible
  },
  set(value) {
    emit('update:visible', value)
  },
})

const estimateSaveTime = computed(() => {
  if (!state.fileCount) return 1.0
  return Math.max(1, 2 * Math.log(30 * state.fileCount)).toFixed(1)
})
watch(internalVisible, (val: boolean, oldValue: boolean) => {
  !val && oldValue && emit('closed')
})

const { t } = useI18n()
const resourceError = async (url: string): Promise<string | undefined> => {
  const { data, error } = await resolveResource(url)
  if (error.value) return error.value?.message || t('resolve-source-base-error')

  if (data.value) {
    const [{ file_size: fileSize }] = data.value.list.resources

    if (!Number(fileSize) && url.startsWith('magnet')) return t('resolve-source-base-error')
  }
}

watchEffect(async () => {
  state.processing = true
  state.status = ResourceStatus.OK

  if (!internalVisible.value || !props.url) return
  try {
    const { data, error } = await resolveResource(props.url)

    if (error.value) {
      state.processing = false
      state.status = error.value.error === 'extremist_content' ? ResourceStatus.LIMITED : ResourceStatus.UNKNOWN
      $track.web_official_input_result({ result: 'fail' })
      return error.value?.message || t('resolve-source-base-error')
    }

    const [
      {
        name,
        file_size: fileSize,
        meta,
        file_count: fileCount,
      },
    ] = data.value.list.resources

    if (!Number(fileSize)) {
      props.url.startsWith('magnet') && $track.web_official_input_result({ result: 'fail' })
      await sleep(2000)
    }

    Object.assign(state, {
      processing: false,
      name,
      size: storageUnit(Number(fileSize)),
      sizeWithoutUnit: Number(fileSize),
      icon: meta.icon,
      fileCount,
      thumbnail: meta.thumbnail_link,
      speedSave: meta.status === '1',
    } as State)

    $track.web_official_input_result({ result: 'pass' })
    $track.web_official_loading_result({
      result: state.thumbnail
        ? state.speedSave
          ? 'pic_show_old'
          : 'pic_show_new'
        : 'pic_fail',
    })
  } catch (err) {
    console.log('resolve resource failure: ', err)
  }
})

defineExpose({
  resourceError,
})
</script>

<style
  lang="less"
  scoped
>
@import 'assets/styles/reset';
@import 'assets/styles/util';

.resolve-source {
  width: 800px;
  margin: 28px 0;
}

.thumbnail-and-operation {
  flex: 1 1 800px;
  display: flex;
  gap: 0 42px;
  align-items: center;
}

.disclaimer {
  margin-top: 16px;
  line-height: 20px;
  font-size: 13px;
  color: #666;

  strong {
    font-weight: normal;
    color: #000;
  }
}

.content {
  width: 480px;
  flex: 0 0 480px;
  height: 338px;
  position: relative;

  border-radius: 20px;

  &.processing, &.embed {
    background: #000;
  }
}

.thumbnail-wrap {
  position: relative;
  height: 100%;
}

.thumbnail {
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  --linear-gradient: linear-gradient(122.65deg, rgba(66, 68, 69, 0.9) 3%, rgba(23, 25, 28, 0.9) 69%);

  > .el-image {
    height: 100%;
    width: 100%;
  }

  &__blur {
    position: absolute;
    height: 100%;
    width: 100%;
    filter: blur(15px);
  }

  &__error, &__placeholder {
    height: 100%;
    background: url('assets/images/home/file-placeholder.svg') var(--no-repeat-center), var(--linear-gradient);
  }

  &__sensitive {
    height: 100%;
    background: url('assets/images/home/sensitive.svg') var(--no-repeat-center), var(--linear-gradient);
  }

  .info {
    padding: 18px;
    color: #fff;
    position: absolute;
    left: 0;
    right: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 99.01%);
    transform: translateY(-100%);
    height: 50%;
    display: flex;
    align-items: flex-end;

    > div {
      display: flex;
      width: 100%;
      align-items: center;
    }
  }

  .file-icon {
    width: 26px;
    height: 26px;
    flex: 0 0 26px;

    + div {
      margin-left: 18px;
    }
  }

  .file-size {
    font-size: 12px;
    line-height: 16px;
    color: rgba(255, 255, 255, 0.6);
  }

  .file-count {
    font-size: 12px;
    line-height: 16px;
    margin-right: 4px;
    color: rgba(255, 255, 255, 0.6);
  }

  .name {
    line-height: 20px;
    #ellipsis();

    + div {
      margin-top: 4px;
    }
  }

}

.close {
  z-index: 1;
  position: absolute;
  top: 16px;
  left: 16px;
  width: 33px;
  height: 33px;
  background: url('assets/images/home/circle-close.svg') var(--no-repeat-center);
}

.watermark {
  z-index: 1;
  position: absolute;
  line-height: 24px;
  background: rgba(68, 68, 68, 0.6);
  color: #fff;
  font-size: 11px;
  padding: 0 12px;
  border-radius: 0 16px 0 12px;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;

  &:before {
    width: 20px;
    height: 20px;
    display: block;
    margin-right: 4px;
    content: url('assets/images/home/watermark-link.svg');
  }
}

.animation {
  position: relative;
  width: 96px;
  margin: auto;
  top: 50%;
  transform: translateY(-50%);
}

.description {
  display: flex;
  flex-direction: column;

  .title {
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;

    .icon {
      display: inline-block;
      vertical-align: bottom;
      position: absolute;
      transform: translateX(-28px);
    }
  }

  .subtitle {
    margin-top: 4px;
    font-weight: 400;
    font-size: 13px;
    line-height: 1.5;
    color: var(--color-secondary-text)
  }

  .pp-spin {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    background: url('assets/images/loading-24x24-dark.svg') var(--no-repeat-center)/cover;
  }

  .processing-text {
    margin-top: 24px;
    display: flex;
    font-weight: 400;
    line-height: 18px;
  }
}

.tips {
  margin-top: 24px;

  li {
    font-weight: 400;
    line-height: 18px;
    display: flex;
    align-items: center;
    gap: 0 8px;

    &:before {
      display: block;
      width: 16px;
      height: 16px;
      flex: 0 0 16px;
      background: var(--no-repeat-center)/cover;
    }

    + li {
      margin-top: 12px;
    }
  }
}

.btn-save-now {
  min-width: 200px;
  padding: 12px 24px;
  display: inline-block;
  line-height: 24px;
  text-align: center;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  margin-top: 32px;
  color: #fff;
  background: var(--color-primary);
  text-decoration: none;

  &:hover {
    color: #fff;
  }

  &.disabled {
    cursor: not-allowed;
    background-color: fade(@color-primary, 50%);
    border-color: var(--el-button-disabled-border-color);
  }
}

@media screen and (min-width: @mobile-break-width) and (max-width: @pc-min-width) {
  .resolve-source {
    flex: none;

    .content {
      height: 288px;
    }
  }

}

</style>
